import { useCallback, useEffect, useState } from 'react'
import type { DocumentRef, RagCollection } from '@saip/backend'
import { useBackend, useHostAdapter } from './context'

export interface UseDocuments {
  collections: RagCollection[]
  docsByCollection: Record<string, DocumentRef[]>
  /** Collection ids enabled "im Kontext" (used by chat @-references). */
  active: Set<string>
  openDocName: string | null
  loading: boolean
  toggleActive: (id: string) => void
  createCollection: (name: string) => Promise<void>
  uploadTo: (collectionId: string, file: File) => Promise<void>
  reload: () => Promise<void>
}

export function useDocuments(): UseDocuments {
  const backend = useBackend()
  const adapter = useHostAdapter()
  const [collections, setCollections] = useState<RagCollection[]>([])
  const [docsByCollection, setDocs] = useState<Record<string, DocumentRef[]>>({})
  const [active, setActive] = useState<Set<string>>(new Set())
  const [openDocName, setOpenDocName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const cols = await backend.rag.listCollections()
      setCollections(cols)
      const entries = await Promise.all(
        cols.map(async (c) => [c.id, (await backend.rag.listDocuments(c.id)).items] as const),
      )
      setDocs(Object.fromEntries(entries))
      const openDoc = await adapter.getActiveDocument()
      setOpenDocName(openDoc?.name ?? null)
      setActive((prev) => (prev.size === 0 && cols[0] ? new Set([cols[0].id]) : prev))
    } finally {
      setLoading(false)
    }
  }, [backend, adapter])

  useEffect(() => {
    void reload()
  }, [reload])

  const toggleActive = useCallback((id: string) => {
    setActive((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const createCollection = useCallback(
    async (name: string) => {
      await backend.rag.createCollection(name)
      await reload()
    },
    [backend, reload],
  )

  const uploadTo = useCallback(
    async (collectionId: string, file: File) => {
      await backend.rag.upload({
        collectionId,
        name: file.name,
        mimeType: file.type,
        data: file,
      })
      await reload()
    },
    [backend, reload],
  )

  return {
    collections,
    docsByCollection,
    active,
    openDocName,
    loading,
    toggleActive,
    createCollection,
    uploadTo,
    reload,
  }
}
